import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/common/DataTable/DataTable';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { MetadataSearchPanel } from '@/components/metadata/MetadataSearchPanel';
import { useAssets, useDeleteAsset, useExportAssets } from '@/api/hooks/useAssets';
import ImportAssetsCsvDialog from '@/components/assets/ImportAssetsCsvDialog';
import { toast } from 'sonner';
import { useLocationsDropdown } from '@/api/hooks/useLocations';
import { Asset } from '@/types/asset.types';
import { getAssetStatusLabel, getAssetStatusColor, tenantConfig } from '@/config/tenant';
import { useFeature } from '@/contexts/FeatureFlagContext';
import { EpcText } from '@/components/common/EpcText';
import {
  Plus,
  Search,
  Upload,
  Download,
  Filter,
  MoreHorizontal,
  Eye,
  Pencil,
  Trash2,
  X,
  Radio,
  ImageIcon,
  FileSpreadsheet,
  ChevronDown,
} from 'lucide-react';

export default function AssetsPage() {
  const { t } = useTranslation('assets');
  const navigate = useNavigate();
  // File import is a module of its own; tenants that never import (TOCA) hide it.
  const assetImportEnabled = useFeature('assetImport');

  // Search and filter state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [metadataFilterAssetIds, setMetadataFilterAssetIds] = useState<string[] | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Delete confirmation state
  const [deleteAsset, setDeleteAsset] = useState<Asset | null>(null);

  // Fetch assets from API
  const {
    data: assetsResponse,
    isLoading,
    error,
  } = useAssets({
    searchStr: searchTerm || undefined,
    status: statusFilter ? parseInt(statusFilter) : undefined,
    locationId: locationFilter ? parseInt(locationFilter) : undefined,
    page,
    pageSize,
  });

  // Fetch locations for filter dropdown
  const { data: locations } = useLocationsDropdown();

  // Delete mutation
  const deleteAssetMutation = useDeleteAsset();

  // Export mutation
  const exportMutation = useExportAssets();

  // Handle export to Excel (respects the current search + filters)
  const handleExport = async () => {
    try {
      const blob = await exportMutation.mutateAsync({
        searchStr: searchTerm || undefined,
        status: statusFilter ? parseInt(statusFilter) : undefined,
        locationId: locationFilter ? parseInt(locationFilter) : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Activos_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t('messages.exported'));
    } catch {
      toast.error(t('messages.errorExport'));
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteAsset) return;
    try {
      await deleteAssetMutation.mutateAsync(deleteAsset.assetId);
      setDeleteAsset(null);
    } catch (error) {
      console.error('Failed to delete asset:', error);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setLocationFilter('');
    setMetadataFilterAssetIds(null);
    setPage(1);
  };

  // Handle metadata search results
  const handleMetadataSearch = (assetIds: string[]) => {
    setMetadataFilterAssetIds(assetIds);
    setPage(1);
  };

  // Clear metadata filter
  const handleMetadataClear = () => {
    setMetadataFilterAssetIds(null);
    setPage(1);
  };

  const hasActiveFilters = searchTerm || statusFilter || locationFilter || metadataFilterAssetIds !== null;

  // Table columns
  const columns = useMemo<ColumnDef<Asset>[]>(
    () => [
      {
        id: 'thumbnail',
        header: '',
        cell: ({ row }) => (
          <div className="w-10 h-10 rounded overflow-hidden bg-muted flex items-center justify-center">
            {row.original.primaryImage ? (
              <img
                src={row.original.primaryImage.imageUrl}
                alt={row.original.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <ImageIcon className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        ),
        size: 50,
      },
      {
        accessorKey: 'name',
        header: t('table.name'),
        cell: ({ row }) => (
          <Link
            to={`/assets/${row.original.assetId}`}
            className="font-medium text-primary hover:underline"
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: 'assetStatusName',
        header: t('table.status'),
        cell: ({ row }) => {
          const statusColor = getAssetStatusColor(row.original.assetStatus);
          return (
            <Badge className={statusColor}>
              {getAssetStatusLabel(row.original.assetStatus)}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'locationName',
        header: t('table.location'),
        cell: ({ row }) => row.original.locationName || '-',
      },
      {
        accessorKey: 'epc',
        header: t('table.epc'),
        cell: ({ row }) =>
          row.original.epc ? (
            <EpcText value={row.original.epc} className="text-xs" />
          ) : (
            <span className="text-muted-foreground flex items-center gap-1">
              <Radio className="h-3 w-3" />
              {t('common:status.pending')}
            </span>
          ),
      },
      {
        accessorKey: 'entity',
        header: t('table.entity'),
        cell: ({ row }) => row.original.entity,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">{t('table.actions')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/assets/${row.original.assetId}`)}>
                <Eye className="mr-2 h-4 w-4" />
                {t('actions.viewDetails')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/assets/${row.original.assetId}/edit`)}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('actions.edit')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDeleteAsset(row.original)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('actions.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [navigate, t]
  );

  // Filter assets based on metadata search results
  const filteredAssets = useMemo(() => {
    if (!assetsResponse?.items) return [];
    if (metadataFilterAssetIds === null) return assetsResponse.items;

    // Filter to only include assets that match the metadata search
    return assetsResponse.items.filter((asset) =>
      metadataFilterAssetIds.includes(String(asset.assetId))
    );
  }, [assetsResponse?.items, metadataFilterAssetIds]);

  // Handle page changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  // Handle search with debounce effect
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('pageTitle')}</h1>
          <p className="text-muted-foreground">{t('pageDescription')}</p>
        </div>
        <div className="flex gap-2">
          {assetImportEnabled && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Upload className="mr-2 h-4 w-4" />
                    {t('importMenu.trigger')}
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <DropdownMenuItem
                    className="items-start"
                    onClick={() => navigate('/assets/import/wizard')}
                  >
                    <FileSpreadsheet className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
                    <div className="flex flex-col">
                      <span>{t('importMenu.wizard.label')}</span>
                      <span className="text-xs text-muted-foreground">
                        {t('importMenu.wizard.hint')}
                      </span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="items-start"
                    onClick={() => setImportDialogOpen(true)}
                  >
                    <Upload className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
                    <div className="flex flex-col">
                      <span>{t('importMenu.csv.label')}</span>
                      <span className="text-xs text-muted-foreground">
                        {t('importMenu.csv.hint')}
                      </span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="items-start"
                    onClick={() => navigate('/assets/import')}
                  >
                    <Upload className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
                    <div className="flex flex-col">
                      <span>{t('importMenu.simple.label')}</span>
                      <span className="text-xs text-muted-foreground">
                        {t('importMenu.simple.hint')}
                      </span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <ImportAssetsCsvDialog
                open={importDialogOpen}
                onOpenChange={setImportDialogOpen}
              />
            </>
          )}
          <Button variant="outline" onClick={handleExport} disabled={exportMutation.isPending}>
            <Download className="mr-2 h-4 w-4" />
            {exportMutation.isPending ? t('common:status.processing') : t('exportAssets')}
          </Button>
          <Link to="/assets/new">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              {t('addAsset')}
            </Button>
          </Link>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('pageTitle')}</CardTitle>
          <CardDescription>{t('pageDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search and Filter Controls */}
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Button
                variant={showFilters ? 'secondary' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="mr-2 h-4 w-4" />
                {t('common:buttons.filter')}
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2">
                    {t('common:status.active')}
                  </Badge>
                )}
              </Button>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="space-y-4">
                {/* Basic Filters */}
                <div className="flex flex-wrap items-center gap-4 p-4 rounded-lg border bg-muted/50">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-sm font-medium mb-1.5 block">{t('filters.status')}</label>
                    <Select value={statusFilter || 'all'} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('status.all')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('status.all')}</SelectItem>
                        {tenantConfig.assetStatuses.map((status) => (
                          <SelectItem key={status.value} value={String(status.value)}>
                            {getAssetStatusLabel(status.value)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex-1 min-w-[200px]">
                    <label className="text-sm font-medium mb-1.5 block">{t('filters.location')}</label>
                    <Select value={locationFilter || 'all'} onValueChange={(v) => { setLocationFilter(v === 'all' ? '' : v); setPage(1); }}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('common:labels.all')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('common:labels.all')}</SelectItem>
                        {locations?.map((location) => (
                          <SelectItem key={location.id} value={String(location.id)}>
                            {location.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-end">
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="mr-2 h-4 w-4" />
                      {t('common:buttons.clear')}
                    </Button>
                  </div>
                </div>

                {/* Metadata Search Panel */}
                <MetadataSearchPanel
                  onSearch={handleMetadataSearch}
                  onClear={handleMetadataClear}
                  isActive={metadataFilterAssetIds !== null}
                />
              </div>
            )}
          </div>

          {/* Data Table */}
          {error ? (
            <div className="text-center py-8 text-destructive">
              {t('common:messages.errorLoading')}
            </div>
          ) : isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredAssets}
              pageCount={metadataFilterAssetIds !== null ? 1 : (assetsResponse?.totalPages ?? 1)}
              pageIndex={metadataFilterAssetIds !== null ? 0 : page - 1}
              pageSize={pageSize}
              onPageChange={(pageIndex) => handlePageChange(pageIndex + 1)}
              onPageSizeChange={handlePageSizeChange}
              totalItems={metadataFilterAssetIds !== null ? filteredAssets.length : (assetsResponse?.totalCount ?? 0)}
              stickyHeader
              maxHeight={showFilters ? 'calc(100vh - 520px)' : 'calc(100vh - 380px)'}
            />
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteAsset}
        onOpenChange={(open) => !open && setDeleteAsset(null)}
        title={t('common:messages.confirmDeleteTitle', { item: t('common:terminology.Asset') })}
        description={t('messages.confirmDelete')}
        confirmLabel={t('common:buttons.delete')}
        cancelLabel={t('common:buttons.cancel')}
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleteAssetMutation.isPending}
      />
    </div>
  );
}
